import { useState, useMemo } from 'react';
import { whatsappTemplates, type WhatsAppTemplate, type TemplateCategory } from '@/mocks/whatsapp_templates';
import { customers as allCustomers } from '@/mocks/customers';
import CustomerCombobox from '@/components/base/CustomerCombobox';

const categories: TemplateCategory[] = [
  "Randevu teyidi",
  "Randevu hatırlatma",
  "Gelmeyen müşteri takip",
  "Paket bitiş hatırlatma",
  "Paket yenileme mesajı",
  "Memnuniyet sorgulama",
  "Kampanya mesajı",
  "Açık ödeme hatırlatma",
  "Kurumsal cevap",
  "Uygunsuz talep reddi",
];

const variableLabels: Record<string, string> = {
  musteri_adi: 'Müşteri Adı',
  tarih: 'Tarih',
  saat: 'Saat',
  paket_adi: 'Paket Adı',
  kalan_seans: 'Kalan Seans',
  hizmet: 'Hizmet',
  kampanya_adi: 'Kampanya Adı',
  kampanya_detayi: 'Kampanya Detayı',
  tutar: 'Tutar',
};

/* ── Toast ── */
interface ToastItem { id: number; message: string; type: 'success' | 'error' | 'info'; }

function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const show = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  };
  const remove = (id: number) => setToasts(prev => prev.filter(t => t.id !== id));
  return { toasts, show, remove };
}

/* ── Helpers ── */
function extractVariables(content: string): string[] {
  const matches = content.match(/\{\{([^}]+)\}\}/g) || [];
  const unique = [...new Set(matches.map(m => m.slice(2, -2)))];
  return unique;
}

function formatPhoneForWhatsApp(phone: string): string {
  return phone.replace(/\D/g, '').replace(/^0/, '');
}

function replaceVariables(content: string, vars: Record<string, string>): string {
  let result = content;
  Object.entries(vars).forEach(([key, value]) => {
    result = result.replace(new RegExp(`\{\{${key}\}\}`, 'g'), value || `{{${key}}}`);
  });
  return result;
}

function getAutoVarValue(varName: string, customer: { fullName: string; phone: string } | null): string {
  if (!customer) return '';
  if (varName === 'musteri_adi') return customer.fullName;
  if (varName === 'telefon') return customer.phone;
  return '';
}

/* ── Send Modal ── */
interface SendModalProps {
  template: WhatsAppTemplate;
  type: 'whatsapp' | 'sms';
  onClose: () => void;
  showToast: (msg: string, type?: 'success' | 'error' | 'info') => void;
}

function SendMessageModal({ template, type, onClose, showToast }: SendModalProps) {
  const [selectedCustomer, setSelectedCustomer] = useState<{ id: string; fullName: string; phone: string } | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>();

  const varNames = useMemo(() => extractVariables(template.content), [template.content]);

  const previewMessage = useMemo(() => {
    return replaceVariables(template.content, {
      ...variables,
      ...(selectedCustomer ? {
        musteri_adi: variables.musteri_adi || selectedCustomer.fullName,
        telefon: variables.telefon || selectedCustomer.phone,
      } : {}),
    });
  }, [template.content, variables, selectedCustomer]);

  const handleSend = () => {
    if (!selectedCustomer) {
      showToast('Lütfen önce bir müşteri seçin', 'error');
      return;
    }
    if (type === 'whatsapp') {
      const phone = formatPhoneForWhatsApp(selectedCustomer.phone);
      if (!phone || phone.length < 10) {
        showToast('Geçersiz telefon numarası', 'error');
        return;
      }
      const text = encodeURIComponent(previewMessage);
      const url = `https://api.whatsapp.com/send?phone=${phone}&text=${text}`;
      window.open(url, '_blank');
      showToast('WhatsApp Web / Uygulaması açıldı', 'success');
    } else {
      showToast('SMS gönderimi için SMS servisi entegrasyonu gereklidir', 'info');
    }
    onClose();
  };

  const handleCustomerChange = (customer: { id: string; fullName: string; phone: string } | null) => {
    setSelectedCustomer(customer);
    if (customer) {
      const auto: Record<string, string> = {};
      varNames.forEach(vn => {
        const autoVal = getAutoVarValue(vn, customer);
        if (autoVal) auto[vn] = autoVal;
      });
      setVariables(prev => ({ ...prev, ...auto }));
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={onClose} />
      <div className="relative bg-theme-card rounded-spa w-full max-w-lg flex flex-col max-h-[90vh] animate-scale-in border overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
        <div className="sticky top-0 flex items-center justify-between p-4 border-b z-10 flex-shrink-0" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={type === 'whatsapp' ? 'ri-whatsapp-line' : 'ri-message-3-line'} style={{ color: type === 'whatsapp' ? '#10B981' : '#3B82F6' }} />
              </div>
              {type === 'whatsapp' ? 'WhatsApp Gönder' : 'SMS Gönder'} — {template.name}
            </div>
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Customer Select */}
          <div>
            <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Gönderilecek Müşteri <span style={{ color: '#EF4444' }}>*</span></label>
            <CustomerCombobox
              customers={allCustomers.map(c => ({ id: c.id, fullName: c.fullName, phone: c.phone, email: c.email, status: c.status }))}
              value={selectedCustomer}
              onChange={handleCustomerChange}
              onCreateNew={(name, phone) => handleCustomerChange({ id: `new-${Date.now()}`, fullName: name, phone })}
              label="Müşteri"
              required
            />
          </div>

          {/* Selected customer info */}
          {selectedCustomer && (
            <div className="flex items-center gap-2 p-2.5 rounded-spa-sm border" style={{ background: 'rgba(16,185,129,0.06)', borderColor: 'rgba(16,185,129,0.2)' }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: '#10B981' }}>
                {selectedCustomer.fullName.charAt(0)}
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{selectedCustomer.fullName}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{selectedCustomer.phone}</p>
              </div>
            </div>
          )}

          {/* Variable inputs */}
          {varNames.length > 0 && (
            <div>
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Şablon Değişkenleri</p>
              <div className="grid grid-cols-2 gap-2">
                {varNames.map(vn => (
                  <div key={vn}>
                    <label className="text-[10px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>
                      {variableLabels[vn] || vn}
                    </label>
                    <input
                      type="text"
                      className="spa-input text-sm"
                      placeholder={variableLabels[vn] || vn}
                      value={variables[vn] || ''}
                      onChange={e => setVariables(prev => ({ ...prev, [vn]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview */}
          <div>
            <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Mesaj Önizleme</p>
            <div className="p-3 rounded-spa-sm text-sm whitespace-pre-wrap border" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)', minHeight: '80px' }}>
              {previewMessage}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 p-4 border-t flex-shrink-0" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <button onClick={onClose} className="spa-btn-secondary text-sm">İptal</button>
          <button
            onClick={handleSend}
            className="spa-btn-primary text-sm flex items-center gap-2"
            style={{ background: type === 'whatsapp' ? '#10B981' : '#3B82F6' }}
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className={type === 'whatsapp' ? 'ri-whatsapp-line' : 'ri-send-plane-fill'} /></div>
            {type === 'whatsapp' ? 'WhatsApp\'ta Aç' : 'SMS Gönder'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit Modal ── */
interface EditModalProps {
  template: WhatsAppTemplate;
  onClose: () => void;
  onSave: (content: string) => void;
}

function EditTemplateModal({ template, onClose, onSave }: EditModalProps) {
  const [content, setContent] = useState(template.content);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={onClose} />
      <div className="relative bg-theme-card rounded-spa w-full max-w-lg flex flex-col max-h-[90vh] animate-scale-in border overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
        <div className="flex items-center justify-between p-5 flex-shrink-0">
          <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Şablon Düzenle</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
            <i className="ri-close-line" />
          </button>
        </div>
        <div className="px-5 pb-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{template.name} — {template.category}</p>
          <textarea className="spa-input min-h-[150px] resize-none" maxLength={1000} value={content} onChange={e => setContent(e.target.value)} />
          <p className="text-[10px] text-right" style={{ color: 'var(--text-muted)' }}>{content.length}/1000</p>
        </div>
        <div className="flex gap-2 p-5 pt-2 flex-shrink-0">
          <button onClick={onClose} className="flex-1 spa-btn-secondary text-sm">İptal</button>
          <button onClick={() => { onSave(content); onClose(); }} className="flex-1 spa-btn-primary text-sm">Kaydet</button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function WhatsAppSMSPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [templateList, setTemplateList] = useState<WhatsAppTemplate[]>(whatsappTemplates);
  const [editTemplate, setEditTemplate] = useState<WhatsAppTemplate | null>(null);
  const [sendModal, setSendModal] = useState<{ open: boolean; template: WhatsAppTemplate | null; type: 'whatsapp' | 'sms' }>({
    open: false,
    template: null,
    type: 'whatsapp',
  });

  const { toasts, show, remove } = useToast();

  const filtered = useMemo(() => {
    let data = [...templateList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(t => t.name.toLowerCase().includes(q) || t.content.toLowerCase().includes(q));
    }
    if (categoryFilter !== 'all') data = data.filter(t => t.category === categoryFilter);
    return data;
  }, [templateList, searchQuery, categoryFilter]);

  const handleToggleActive = (id: string) => {
    setTemplateList(prev => prev.map(t => t.id === id ? { ...t, active: !t.active } : t));
    const tpl = templateList.find(t => t.id === id);
    show(tpl?.active ? 'Şablon pasif yapıldı' : 'Şablon aktif yapıldı', 'success');
  };

  const handleSaveEdit = (content: string) => {
    if (!editTemplate) return;
    setTemplateList(prev => prev.map(t => t.id === editTemplate.id ? { ...t, content } : t));
    setEditTemplate(null);
    show('Şablon güncellendi', 'success');
  };

  const openSend = (template: WhatsAppTemplate, type: 'whatsapp' | 'sms') => {
    setSendModal({ open: true, template, type });
  };

  return (
    <div className="space-y-4 animate-fade-in relative">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[200] space-y-2">
        {toasts.map(t => (
          <div
            key={t.id}
            onClick={() => remove(t.id)}
            className="px-4 py-3 rounded-spa text-sm font-medium cursor-pointer animate-fade-in flex items-center gap-2 border"
            style={{
              background: t.type === 'success' ? '#10B981' : t.type === 'error' ? '#EF4444' : '#3B82F6',
              color: '#FFFFFF',
              borderColor: t.type === 'success' ? '#059669' : t.type === 'error' ? '#DC2626' : '#2563EB',
            }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={t.type === 'success' ? 'ri-check-line' : t.type === 'error' ? 'ri-error-warning-line' : 'ri-information-line'} />
            </div>
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>WhatsApp / SMS Şablonları</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{filtered.length} şablon kaydı</p>
        </div>
        <button className="spa-btn-primary text-sm flex items-center gap-2 self-start sm:self-auto" onClick={() => show('Yeni şablon ekleme modalı henüz yapılandırılmadı', 'info')}>
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
          Yeni Şablon
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
          <input className="spa-input pl-10" placeholder="Şablon adı veya içerik ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <select className="spa-input text-sm min-w-[160px]" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
          <option value="all">Tüm Kategoriler</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Templates */}
      <div className="grid grid-cols-1 gap-3">
        {filtered.map(tpl => (
          <div key={tpl.id} className="spa-card p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-spa-sm flex items-center justify-center flex-shrink-0" style={{ background: tpl.active ? 'rgba(16,185,129,0.12)' : 'rgba(156,163,175,0.12)' }}>
                  <i className="ri-whatsapp-line" style={{ color: tpl.active ? '#10B981' : '#9CA3AF' }} />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{tpl.name}</p>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: tpl.active ? 'rgba(16,185,129,0.12)' : 'rgba(156,163,175,0.12)', color: tpl.active ? '#10B981' : '#9CA3AF' }}>
                      {tpl.active ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{tpl.category}</p>
                </div>
              </div>
              <button
                onClick={() => handleToggleActive(tpl.id)}
                className="w-8 h-8 rounded-md flex items-center justify-center cursor-pointer"
                style={{ background: 'var(--bg-tertiary)' }}
                title={tpl.active ? 'Pasif Yap' : 'Aktif Yap'}
              >
                <i className={tpl.active ? 'ri-eye-line' : 'ri-eye-off-line'} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>

            <div className="p-3 rounded-spa-sm text-sm" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
              {tpl.content}
            </div>

            <div className="flex gap-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button onClick={() => setEditTemplate(tpl)} className="spa-btn-secondary text-xs py-2 px-3">
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-edit-line text-xs mr-1" /></div>
                Düzenle
              </button>
              <button
                onClick={() => openSend(tpl, 'whatsapp')}
                className="px-3 py-2 rounded-spa-sm text-xs font-medium cursor-pointer flex items-center gap-1.5 transition-all hover:brightness-95"
                style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}
              >
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-whatsapp-line text-xs" /></div>
                WhatsApp Gönder
              </button>
              <button
                onClick={() => openSend(tpl, 'sms')}
                className="px-3 py-2 rounded-spa-sm text-xs font-medium cursor-pointer flex items-center gap-1.5 transition-all hover:brightness-95"
                style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6' }}
              >
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-message-3-line text-xs" /></div>
                SMS Gönder
              </button>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-whatsapp-line text-xl" style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Şablon bulunamadı</p>
        </div>
      )}

      {/* Send Modal */}
      {sendModal.open && sendModal.template && (
        <SendMessageModal
          template={sendModal.template}
          type={sendModal.type}
          onClose={() => setSendModal({ open: false, template: null, type: 'whatsapp' })}
          showToast={show}
        />
      )}

      {/* Edit Modal */}
      {editTemplate && (
        <EditTemplateModal
          template={editTemplate}
          onClose={() => setEditTemplate(null)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
}